import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { axiosMaster } from "../api/axiosInstance";

const AddEditEmployeeMaster = ({ editingData, onClose, onSuccess }) => {
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const restId = storedUser?.restaurantId || "";
  const groupId = storedUser?.groupId || 1;
  const userId = storedUser?.userId || 0;

  /* ================= STATE ================= */
  const [empName, setEmpName] = useState("");
  const [empMobileNo, setEmpMobileNo] = useState("");
  const [empType, setEmpType] = useState("Staff");
  const [empStatus, setEmpStatus] = useState("Working");
  const [joiningDate, setJoiningDate] = useState("");

  const [deptOptions, setDeptOptions] = useState([]);
  const [desigOptions, setDesigOptions] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDesig, setSelectedDesig] = useState(null);

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loadingRest, setLoadingRest] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= ADDRESS FIELDS ================= */
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [landMark, setLandMark] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [pinCode, setPinCode] = useState("");

  /* ================= REFS ================= */
  const restaurantRef = useRef(null);
  const nameRef = useRef(null);
  const mobileRef = useRef(null);
  const deptRef = useRef(null);
  const desigRef = useRef(null);
  const typeRef = useRef(null);
  const dateRef = useRef(null);
  const statusRef = useRef(null);
  const addressRefs = {
    line1: useRef(null),
    line2: useRef(null),
    landMark: useRef(null),
    city: useRef(null),
    state: useRef(null),
    pin: useRef(null),
  };
  const saveRef = useRef(null);

  /* ================= HELPERS ================= */
  const toTitleCase = (text) =>
    text
      ? text
          .toLowerCase()
          .split(" ")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "";

  const handleFocusSelect = (ref) => setTimeout(() => ref?.current?.select?.(), 0);

  /* ================= FETCH ================= */
  const fetchDepartments = async () => {
    try {
      const res = await axiosMaster.get(`/DepartmentMaster/GetByGroup?groupId=${groupId}`);
      setDeptOptions((res.data || []).map((d) => ({ value: d.departmentId, label: d.departmentName })));
    } catch (err) {
      console.error("Department fetch error:", err);
    }
  };

  const fetchDesignations = async () => {
    try {
      const res = await axiosMaster.get(`/DesignationMaster/GetByGroup?groupId=${groupId}`);
      setDesigOptions((res.data || []).map((d) => ({ value: d.designationId, label: d.designationName })));
    } catch (err) {
      console.error("Designation fetch error:", err);
    }
  };

  const fetchRestaurants = async () => {
    try {
      setLoadingRest(true);
      const res = await axiosMaster.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);
      const options = (res.data || []).map((r) => ({ value: r.restId, label: r.restaurantName }));
      setRestaurants(options);

      const defaultId = editingData?.restId || restId;
      const found = options.find((x) => Number(x.value) === Number(defaultId));
      if (found) setSelectedRestaurant(found);
      else if (options.length) setSelectedRestaurant(options[0]);
    } catch (err) {
      console.error("Restaurant fetch error:", err);
    } finally {
      setLoadingRest(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchDesignations();
    fetchRestaurants();
  }, []);

  /* ================= LOAD EDIT ================= */
  useEffect(() => {
    if (!editingData) return;

    setEmpName(editingData.empName || "");
    setEmpMobileNo(editingData.empMobileNo || "");
    setEmpType(editingData.empType || "Staff");
    setEmpStatus(editingData.empStatus || "Working");
    setJoiningDate(
      editingData.joiningDate !== "0001-01-01T00:00:00.000Z"
        ? editingData.joiningDate.substring(0, 10)
        : ""
    );

    setAddressLine1(editingData.addressLine1 || "");
    setAddressLine2(editingData.addressLine2 || "");
    setLandMark(editingData.landMark || "");
    setCity(editingData.city || "");
    setStateField(editingData.state || "");
    setPinCode(editingData.pinCode || "");
  }, [editingData]);

  useEffect(() => {
    if (!editingData) return;

    if (deptOptions.length && editingData.departmentId) {
      const dept = deptOptions.find((d) => d.value === editingData.departmentId);
      setSelectedDept(dept || null);
    }

    if (desigOptions.length && editingData.designationId) {
      const desig = desigOptions.find((d) => d.value === editingData.designationId);
      setSelectedDesig(desig || null);
    }
  }, [deptOptions, desigOptions, editingData]);

  /* ================= AUTO FOCUS ================= */
  useEffect(() => {
    setTimeout(() => {
      restaurantRef.current?.focus?.();
    }, 300);
  }, []);

  /* ================= KEYBOARD NAV ================= */
  const handleKeyDown = (e, nextRef, prevRef, options = {}) => {
    const { mobileCheck = false, isLast = false } = options;

    const isReactSelectInput =
      e.target?.id?.includes("react-select") ||
      e.target?.className?.includes("react-select");

    if (isReactSelectInput && e.key === "Enter") return;

    if (mobileCheck && e.key === "Enter" && empMobileNo.length !== 10) {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
      else if (isLast) handleSubmit();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      if (prevRef?.current) prevRef.current.focus();
      else onClose?.();
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (loading) return;

    if (!selectedRestaurant) return alert("Select restaurant");
    if (!empName.trim()) return alert("Enter employee name");
    if (!selectedDept) return alert("Select department");
    if (!selectedDesig) return alert("Select designation");

    setLoading(true);
    try {
      await axiosMaster.post("/EmployeeMaster/UpsertEmployee", {
        groupId,
        restId: selectedRestaurant.value,
        activeRestId: selectedRestaurant.value,
        empId: editingData?.empId || 0,
        empName: toTitleCase(empName.trim()),
        empMobileNo,
        empType: toTitleCase(empType.trim()),
        empDepartmentId: selectedDept.value,
        empDesignationId: selectedDesig.value,
        empJoiningDate: joiningDate ? new Date(joiningDate).toISOString() : new Date().toISOString(),
        empStatus: toTitleCase(empStatus.trim()),
        addressLine1,
        addressLine2,
        landMark,
        city,
        state: stateField,
        pinCode,
        userId,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Save error:", err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-3">
      <div
        className="bg-white w-full max-w-3xl md:max-w-xl rounded-xl shadow-xl p-6 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold mb-4">{editingData ? "Edit Employee" : "Add Employee"}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Restaurant */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Restaurant *</label>
            <Select
              ref={restaurantRef}
              options={restaurants}
              value={selectedRestaurant}
              onChange={(option) => {
                setSelectedRestaurant(option);
                setTimeout(() => nameRef.current?.focus(), 60);
              }}
              onKeyDown={(e) => handleKeyDown(e, nameRef, null)}
              isLoading={loadingRest}
              placeholder="Select restaurant"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              isSearchable
              openMenuOnFocus={true}
              tabSelectsValue={false}
              menuPosition="fixed"
            />
          </div>

          {/* Name */}
          <InputText
            label="Employee Name *"
            value={empName}
            setValue={setEmpName}
            refProp={nameRef}
            nextRef={mobileRef}
            prevRef={restaurantRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpName(toTitleCase(empName))}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Mobile */}
          <InputText
            label="Mobile *"
            value={empMobileNo}
            setValue={(val) => setEmpMobileNo(val.replace(/\D/g, "").slice(0, 10))}
            refProp={mobileRef}
            nextRef={deptRef}
            prevRef={nameRef}
            handleKeyDown={(e) => handleKeyDown(e, deptRef, nameRef, { mobileCheck: true })}
            handleBlurFormat={null}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Department */}
          <SelectField
            label="Department *"
            options={deptOptions}
            value={selectedDept}
            refProp={deptRef}
            nextRef={desigRef}
            prevRef={mobileRef}
            setValue={setSelectedDept}
            handleKeyDown={handleKeyDown}
          />

          {/* Designation */}
          <SelectField
            label="Designation *"
            options={desigOptions}
            value={selectedDesig}
            refProp={desigRef}
            nextRef={typeRef}
            prevRef={deptRef}
            setValue={setSelectedDesig}
            handleKeyDown={handleKeyDown}
          />

          {/* Type */}
          <InputText
            label="Employee Type"
            value={empType}
            setValue={setEmpType}
            refProp={typeRef}
            nextRef={dateRef}
            prevRef={desigRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpType(toTitleCase(empType))}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Joining Date */}
          <InputDate
            label="Joining Date"
            value={joiningDate}
            setValue={setJoiningDate}
            refProp={dateRef}
            nextRef={statusRef}
            prevRef={typeRef}
            handleKeyDown={handleKeyDown}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Status */}
          <InputText
            label="Employee Status"
            value={empStatus}
            setValue={setEmpStatus}
            refProp={statusRef}
            nextRef={addressRefs.line1}
            prevRef={dateRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpStatus(toTitleCase(empStatus))}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Address Fields */}
          <InputText
            label="Address Line 1"
            value={addressLine1}
            setValue={setAddressLine1}
            refProp={addressRefs.line1}
            nextRef={addressRefs.line2}
            prevRef={statusRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setAddressLine1(toTitleCase(addressLine1))}
            handleFocusSelect={handleFocusSelect}
          />

          <InputText
            label="Address Line 2"
            value={addressLine2}
            setValue={setAddressLine2}
            refProp={addressRefs.line2}
            nextRef={addressRefs.landMark}
            prevRef={addressRefs.line1}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setAddressLine2(toTitleCase(addressLine2))}
            handleFocusSelect={handleFocusSelect}
          />

          <InputText
            label="Landmark"
            value={landMark}
            setValue={setLandMark}
            refProp={addressRefs.landMark}
            nextRef={addressRefs.city}
            prevRef={addressRefs.line2}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setLandMark(toTitleCase(landMark))}
            handleFocusSelect={handleFocusSelect}
          />

          <InputText
            label="City"
            value={city}
            setValue={setCity}
            refProp={addressRefs.city}
            nextRef={addressRefs.state}
            prevRef={addressRefs.landMark}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setCity(toTitleCase(city))}
            handleFocusSelect={handleFocusSelect}
          />

          <InputText
            label="State"
            value={stateField}
            setValue={setStateField}
            refProp={addressRefs.state}
            nextRef={addressRefs.pin}
            prevRef={addressRefs.city}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setStateField(toTitleCase(stateField))}
            handleFocusSelect={handleFocusSelect}
          />

          <InputText
            label="Pin Code"
            value={pinCode}
            setValue={(val) => setPinCode(val.replace(/\D/g, "").slice(0, 6))}
            refProp={addressRefs.pin}
            nextRef={saveRef}
            prevRef={addressRefs.state}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={null}
            handleFocusSelect={handleFocusSelect}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            ref={saveRef}
            onClick={handleSubmit}
            onKeyDown={(e) => handleKeyDown(e, null, addressRefs.pin, { isLast: true })}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= SMALL INPUT COMPONENTS ================= */
const InputText = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleBlurFormat, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlurFormat}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

const InputDate = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      type="date"
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

const SelectField = ({ label, options, value, refProp, nextRef, prevRef, setValue, handleKeyDown }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <Select
      ref={refProp}
      options={options}
      value={value}
      onChange={(val) => {
        setValue(val);
        if (nextRef?.current) nextRef.current.focus();
      }}
      placeholder={`Select ${label}`}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      isSearchable
      tabSelectsValue={false}
      openMenuOnFocus={true}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
    />
  </div>
);

export default AddEditEmployeeMaster;